/**
 * Document Settings Defaults and Normalization
 * Single source of truth for document settings with validation and normalization
 */

// Default column settings (base template)
export const DEFAULT_COLUMN_SETTINGS = {
    // Legacy general body size (fallback for older settings / PDF)
    textSize: 11,
    // Per-section font sizes
    companyInfoTextSize: 16,
    documentTitleTextSize: 20,
    clientDetailsTextSize: 13,
    tableTextSize: 11,
    totalsTextSize: 13,
    profilePictureShape: 'NoChooseShape',
    boldDate: false,
    width: 100, // mm - logo slot width
    height: 100, // mm - logo slot height (same as width for square by default)
    signatureWidth: 80, // mm - signature slot width
    signatureHeight: 40, // mm - signature slot height
    backgroundColor: '#ffffff',
    textColor: '#000000',
    // Per-section text colors
    companyInfoTextColor: '#000000',
    clientDetailsTextColor: '#000000',
    tableTextColor: '#000000',
    totalsTextColor: '#000000',
    upperPanelBackgroundColor: '#fbfbfb',
    svgColor: '#F9AA2A',
    svgShape: 'corner',
    upperPanelColor: '#fbfbfb',
    svgImageColor: '#F9AA2A',
    logoPosition: 'left', // 'center' or 'left'
    dateAlign: 'left', // 'left' or 'right'
    titleTextColor: '#000000',
    // Accent chrome: table header, פריטים, סה״כ לתשלום highlight, footer
    accentColor: '#1B3A6B',
    totalsLayout: 'horizontal', // 'horizontal' or 'vertical'
    documentFont: 'Arial', // font family for document body and headings
    // High-level visual template (classic | greenMinimal | greenProfessional | corporateBlue | modernBanner | softWaves)
    documentStyleVersion: 'classic',
    // { [styleId]: { ...layout-controlled keys } } — values last used on each layout, restored on switch back
    styleCustomizations: null,
    // Full-page background image URL (S3 / pre-signed) and opacity 0–1
    pageBackgroundImage: '',
    pageBackgroundOpacity: 0.18,
    // Background position (% of page) and rotation (degrees)
    pageBackgroundOffsetX: 0,
    pageBackgroundOffsetY: 0,
    pageBackgroundRotation: 0,
    // Business header shown on documents: { fields: [{ id, type, label, value, visible }] }.
    // null = legacy rows that still render live client data.
    headerDetails: null,
};

// Default document settings
export const DEFAULT_DOCUMENT_SETTINGS = {
    customDescription: '',
    customFooter: '',
    headerRightText: '',
    headerLeftText: '',
    itemsDisplayMode: '',
    columnSettings: DEFAULT_COLUMN_SETTINGS,
};

// Validation ranges
export const SETTINGS_RANGES = {
    textSize: { min: 8, max: 16 },
    companyInfoTextSize: { min: 12, max: 24 },
    documentTitleTextSize: { min: 16, max: 32 },
    clientDetailsTextSize: { min: 10, max: 20 },
    tableTextSize: { min: 8, max: 16 },
    totalsTextSize: { min: 10, max: 20 },
    width: { min: 40, max: 120 }, // mm
    height: { min: 40, max: 120 }, // mm
    signatureWidth: { min: 40, max: 150 }, // mm
    signatureHeight: { min: 20, max: 80 }, // mm
    pageBackgroundOpacity: { min: 0.05, max: 0.6 },
    pageBackgroundOffsetX: { min: -50, max: 50 },
    pageBackgroundOffsetY: { min: -50, max: 50 },
    pageBackgroundRotation: { min: -180, max: 180 },
};

/**
 * Clamp a value to a range
 */
const clamp = (value, min, max) => {
    if (value == null) return null;
    const num = Number(value);
    if (isNaN(num)) return null;
    return Math.max(min, Math.min(max, num));
};

export const HEADER_FIELD_TYPES = ['businessName', 'taxId', 'address', 'phone', 'phone2', 'email', 'website', 'custom'];

const isPhoneHeaderType = (type) => type === 'phone' || type === 'phone2';

/**
 * Whether a header field prints on the same line as the field before it. Headers saved before
 * `joinPrevious` existed keep the old rule (adjacent phones share a line). The business name is
 * printed on its own, so it never joins and nothing joins onto it.
 * Mirrors DocumentDesignResolver.JoinsPrevious on the server.
 */
export const headerFieldJoinsPrevious = (field, previousField) => {
    if (!field || !previousField || field.type === 'businessName' || previousField.type === 'businessName') {
        return false;
    }
    if (typeof field.joinPrevious === 'boolean') return field.joinPrevious;
    return isPhoneHeaderType(field.type) && isPhoneHeaderType(previousField.type);
};

/**
 * Split header fields into printed rows: `[{ fields: [...], startIndex }]`. Business name fields
 * are left out. Rows keep hidden/empty fields so the editor can show them; printing skips them.
 */
export const groupHeaderFieldRows = (fields = []) => {
    const rows = [];
    fields.forEach((field, index) => {
        if (!field || field.type === 'businessName') return;
        const last = rows[rows.length - 1];
        if (last && last.endIndex === index - 1 && headerFieldJoinsPrevious(field, fields[index - 1])) {
            last.fields.push(field);
            last.endIndex = index;
        } else {
            rows.push({ fields: [field], startIndex: index, endIndex: index });
        }
    });
    return rows;
};

/**
 * Normalize header details to `{ fields: [...] }` or null when absent/invalid.
 */
export const normalizeHeaderDetails = (headerDetails) => {
    if (!headerDetails || typeof headerDetails !== 'object' || !Array.isArray(headerDetails.fields)) {
        return null;
    }
    const raw = headerDetails.fields.filter((f) => f && typeof f === 'object');
    const fields = raw.map((f, index) => ({
        id: String(f.id || `${f.type || 'custom'}-${index}`),
        type: HEADER_FIELD_TYPES.includes(f.type) ? f.type : 'custom',
        label: f.label == null ? '' : String(f.label),
        value: f.value == null ? '' : String(f.value),
        visible: f.visible !== false,
        joinPrevious: headerFieldJoinsPrevious(f, raw[index - 1]),
    }));
    return { fields };
};

/**
 * Normalize and validate column settings
 * @param {Object} columnSettings - Raw column settings from API
 * @returns {Object} Normalized column settings
 */
export const normalizeColumnSettings = (columnSettings = {}) => {
    const normalized = { ...DEFAULT_COLUMN_SETTINGS };
    
    // Merge provided settings
    Object.keys(DEFAULT_COLUMN_SETTINGS).forEach(key => {
        const value = columnSettings[key];
        
        // Skip null/undefined values (use default)
        if (value == null) {
            return;
        }
        
        // Validate numeric ranges
        if (SETTINGS_RANGES[key]) {
            const clamped = clamp(value, SETTINGS_RANGES[key].min, SETTINGS_RANGES[key].max);
            if (clamped != null) {
                normalized[key] = clamped;
            }
        } else {
            // For non-numeric values, use as-is if valid
            normalized[key] = value;
        }
    });
    
    // Ensure width and height are valid numbers
    if (normalized.width == null || normalized.width <= 0) {
        normalized.width = DEFAULT_COLUMN_SETTINGS.width;
    }
    if (normalized.height == null || normalized.height <= 0) {
        normalized.height = DEFAULT_COLUMN_SETTINGS.height;
    }

    // Legacy settings only had textSize — seed section sizes when missing
    const legacyText = columnSettings.clientDetailsTextSize == null
        || columnSettings.tableTextSize == null
        || columnSettings.totalsTextSize == null
        ? Number(columnSettings.textSize) || DEFAULT_COLUMN_SETTINGS.textSize
        : null;
    if (columnSettings.clientDetailsTextSize == null && legacyText != null) {
        normalized.clientDetailsTextSize = clamp(legacyText, SETTINGS_RANGES.clientDetailsTextSize.min, SETTINGS_RANGES.clientDetailsTextSize.max)
            ?? DEFAULT_COLUMN_SETTINGS.clientDetailsTextSize;
    }
    if (columnSettings.tableTextSize == null && legacyText != null) {
        normalized.tableTextSize = clamp(legacyText, SETTINGS_RANGES.tableTextSize.min, SETTINGS_RANGES.tableTextSize.max)
            ?? DEFAULT_COLUMN_SETTINGS.tableTextSize;
    }
    if (columnSettings.totalsTextSize == null && legacyText != null) {
        normalized.totalsTextSize = clamp(Math.max(legacyText, 12), SETTINGS_RANGES.totalsTextSize.min, SETTINGS_RANGES.totalsTextSize.max)
            ?? DEFAULT_COLUMN_SETTINGS.totalsTextSize;
    }
    if (columnSettings.companyInfoTextColor == null && columnSettings.titleTextColor) {
        normalized.companyInfoTextColor = columnSettings.titleTextColor;
    }
    if (columnSettings.clientDetailsTextColor == null && columnSettings.textColor) {
        normalized.clientDetailsTextColor = columnSettings.textColor;
    }
    if (columnSettings.tableTextColor == null && columnSettings.textColor) {
        normalized.tableTextColor = columnSettings.textColor;
    }
    if (columnSettings.totalsTextColor == null && columnSettings.textColor) {
        normalized.totalsTextColor = columnSettings.textColor;
    }

    // Xtram Business: legacy soft-blue header band → white default
    if (
        String(normalized.documentStyleVersion || '').toLowerCase() === 'corporateblue'
        && String(normalized.upperPanelColor || '').trim().toLowerCase() === '#e8f0f8'
    ) {
        normalized.upperPanelColor = '#FFFFFF';
    }

    normalized.headerDetails = normalizeHeaderDetails(columnSettings.headerDetails);

    const customizations = columnSettings.styleCustomizations;
    normalized.styleCustomizations = customizations && typeof customizations === 'object' && !Array.isArray(customizations)
        ? customizations
        : null;

    return normalized;
};

/**
 * Normalize full document settings
 * @param {Object} settings - Raw settings from API
 * @returns {Object} Normalized settings
 */
export const normalizeDocumentSettings = (settings = {}) => {
    return {
        customDescription: settings.customDescription || settings.constantClientDescription || '',
        customFooter: settings.customFooter || settings.constantInvoiceDescription || '',
        headerRightText: settings.headerRightText || '',
        headerLeftText: settings.headerLeftText || '',
        itemsDisplayMode: settings.itemsDisplayMode || '',
        columnSettings: normalizeColumnSettings(settings.columnSettings),
    };
};

/**
 * Convert mm to px for screen preview (approximate: 1mm ≈ 3.7795px at 96dpi)
 * For more accurate conversion, use: mm * (96 / 25.4)
 */
export const mmToPx = (mm) => {
    if (!mm || mm <= 0) return 0;
    return mm * (96 / 25.4);
};

/**
 * Get logo slot dimensions in px for preview
 * @param {Object} columnSettings - Column settings
 * @returns {Object} { widthPx, heightPx }
 */
export const getLogoSlotDimensions = (columnSettings) => {
    const widthMm = columnSettings?.width || DEFAULT_COLUMN_SETTINGS.width;
    const heightMm = columnSettings?.height || DEFAULT_COLUMN_SETTINGS.height;
    
    // Convert mm to px for screen preview
    // Use larger multiplier so logo appears prominent and fills container
    // The image will maintain its original aspect ratio via object-fit: contain
    const MAX_SLOT_WIDTH = 250; // px - allow larger logos
    const MAX_SLOT_HEIGHT = 140; // px (must fit in 150px header with padding)
    
    // Scale based on slider value: larger multiplier for more visible logo
    // When slider = 40mm → ~96px, when slider = 120mm → 250px (capped)
    const isRelativeSizing = widthMm === heightMm;
    let widthPx = widthMm * 2.0; // Larger multiplier: slider value directly affects size
    let heightPx = isRelativeSizing 
        ? heightMm * 2.0  // Same ratio for square sizing
        : heightMm * 1.5; // Slightly smaller for non-relative
    
    // Cap maximum dimensions to prevent overflow from header
    if (widthPx > MAX_SLOT_WIDTH) {
        widthPx = MAX_SLOT_WIDTH;
    }
    if (heightPx > MAX_SLOT_HEIGHT) {
        heightPx = MAX_SLOT_HEIGHT;
    }
    
    return {
        widthPx: Math.round(widthPx),
        heightPx: Math.round(heightPx),
        widthMm,
        heightMm,
    };
};

/**
 * Get object-fit value based on settings and shape
 * Use 'contain' to show full logo without cropping for professional appearance
 * @param {Object} columnSettings - Column settings
 * @returns {string} 'contain'
 */
export const getObjectFit = (columnSettings) => {
    // Always use 'contain' to show the full logo without cutting off parts
    // The logo will be scaled to fit within the container while maintaining aspect ratio
    // This ensures the complete logo is visible and looks professional
    return 'contain';
};

/**
 * Get signature slot dimensions in px for preview
 * @param {Object} columnSettings - Column settings
 * @returns {Object} { widthPx, heightPx }
 */
export const getSignatureSlotDimensions = (columnSettings) => {
    const widthMm = columnSettings?.signatureWidth || DEFAULT_COLUMN_SETTINGS.signatureWidth;
    const heightMm = columnSettings?.signatureHeight || DEFAULT_COLUMN_SETTINGS.signatureHeight;
    
    // Convert to px for screen preview
    const MAX_SIGNATURE_WIDTH = 300; // px
    const MAX_SIGNATURE_HEIGHT = 150; // px
    
    // Use multiplier for signature size
    let widthPx = widthMm * 2.0;
    let heightPx = heightMm * 2.0;
    
    // Cap maximum dimensions
    if (widthPx > MAX_SIGNATURE_WIDTH) {
        widthPx = MAX_SIGNATURE_WIDTH;
    }
    if (heightPx > MAX_SIGNATURE_HEIGHT) {
        heightPx = MAX_SIGNATURE_HEIGHT;
    }
    
    return {
        widthPx: Math.round(widthPx),
        heightPx: Math.round(heightPx),
        widthMm,
        heightMm,
    };
};

